import cx from 'classnames';
import {memo, useEffect, useMemo, useRef, useState} from 'react';
import Skeleton from 'react-loading-skeleton';
import {useIsLargishAndUp} from '~/common/hooks/useMatchQuery';
import {useImageProxySizer} from '~/ViewInvitation/hooks/useImageProxySizer';
import {PHOTO_FAILED_REASON_OTHER, PHOTO_STATUS} from '~/ViewInvitation/reducers/constants';
import {isHost} from '~/ViewInvitation/utils';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {AlertLineIcon} from '~library/svg/AlertLineIcon';
import {MoreIcon} from '~library/svg/MoreIcon';
import {TrashIcon} from '~library/svg/TrashIcon';
import {StarIcon} from '~library/svg/v2/StarIcon';
import {StarLineIcon} from '~library/svg/v2/StarLineIcon';
import {charcoal, feedbackError, white} from '~libSass/base/_exports.sass';

export const FeaturedLabel = ({isContextMenu = false}) => (
  <div
    className={cx('photo-gallery-tile__featured-label', {
      'photo-gallery-tile__featured-label-context-menu': isContextMenu,
    })}
  >
    <StarIcon ratio={0.5} color={white} />
  </div>
);

export const PhotoGalleryTile = memo(
  ({
    photo,
    featured = false,
    isHostModule,
    onViewPhotoFn,
    handleFeaturePhoto,
    handleDeletePhoto,
    handleOpenContextMenu,
  }) => {
    const isStaged = !!photo.file;
    const isFailure = isStaged ? photo?.status === PHOTO_STATUS.ERROR : false;
    const {getThumbnailUrl} = useImageProxySizer();
    const isLargishAndUp = useIsLargishAndUp();
    const [src, setSrc] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const photoRef = useRef();

    useEffect(() => {
      let url = photo?.content?.url || photo?.url || undefined;

      if (url) {
        setSrc(getThumbnailUrl(url));
        // biome-ignore lint/suspicious/noEmptyBlockStatements: allow empty
        return () => {};
      }

      const obs = new IntersectionObserver(([e]) => {
        if (e.isIntersecting) {
          url = URL.createObjectURL(photo.file);
          setSrc(url);
          obs.disconnect();
        }
      });
      obs.observe(photoRef.current);

      return () => {
        obs.disconnect();
        if (url) URL.revokeObjectURL(url);
      };
    }, [photo, getThumbnailUrl]);

    const tileActions = useMemo(
      () => (
        <>
          {featured && isHostModule && <FeaturedLabel />}
          {isLargishAndUp
            ? <>
                {!featured && isHostModule && (
                  <Button
                    className="photo-gallery__photo-action photo-gallery__photo-action__feature"
                    variant="unstyled-button"
                    data-qa-id="feature-photo"
                    aria-label="Feature photo"
                    blurry
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFeaturePhoto(photo, isStaged);
                    }}
                  >
                    <StarLineIcon ratio={0.5} />
                  </Button>
                )}
                <Button
                  className="photo-gallery__photo-action photo-gallery__photo-action__delete"
                  variant="unstyled-button"
                  data-qa-id="delete-photo"
                  aria-label="Delete photo"
                  blurry
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeletePhoto(photo.id);
                  }}
                >
                  <TrashIcon ratio={0.5} />
                </Button>
              </>
            : <Button
                className="photo-gallery__photo-action photo-gallery__photo-action__open-menu-mw"
                variant="unstyled-button"
                data-qa-id="open-context-menu"
                aria-label="Open context menu"
                blurry
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenContextMenu(photo, isStaged, src, featured);
                }}
              >
                <MoreIcon ratio={0.75} color={charcoal} />
              </Button>}
        </>
      ),
      [
        handleFeaturePhoto,
        handleOpenContextMenu,
        handleDeletePhoto,
        isLargishAndUp,
        isHostModule,
        photo,
        featured,
        isStaged,
        src,
      ]
    );

    return (
      // biome-ignore lint/a11y/useAriaPropsSupportedByRole: no label on div
      <div
        className="photo-gallery-tile"
        data-qa-id="photo-gallery-tile"
        aria-label="Photo gallery tile"
        ref={photoRef}
      >
        {src && (
          <div className="photo-gallery-tile__content">
            <img
              alt=""
              src={src}
              data-qa-id="photo-gallery-tile-img"
              className="photo-gallery-tile__img"
              loading="lazy"
              onLoad={() => setIsLoading(false)}
            />
            {isFailure && (
              <div className="photo-gallery-tile__feedback">
                <AlertLineIcon color={feedbackError} ratio={1} />
                <Typography variant="paragraph2" color="ghost" semibold>
                  {photo?.reason || PHOTO_FAILED_REASON_OTHER}
                </Typography>
              </div>
            )}
            {isHost() && tileActions}
            <Button
              variant="unstyled-button"
              onClick={() => onViewPhotoFn(photo.id)}
              className="photo-gallery-tile-trigger"
              data-qa-id="photo-gallery-tile-trigger"
              aria-label="Open photo in photo viewer"
            />
          </div>
        )}
        {isLoading && (
          <Skeleton
            containerClassName="photo-gallery__skeleton-wrapper"
            className="photo-gallery__skeleton"
          />
        )}
      </div>
    );
  }
);
